<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, HTMLAttributes } from 'svelte/elements';
	import { Popover } from 'bits-ui';
	import {
		POP_OUT_ALIGN,
		POP_OUT_DEFAULT_OFFSET,
		POP_OUT_SIDE,
		PORTAL_CONTAINER_SELECTOR
	} from '$lib/constants';
	import { arrowKeyNavigation } from '$lib/actions/arrow-key-navigation.svelte';
	import { overlayStore } from '$lib/stores/overlay.svelte';
	import { layerProps } from '../layer-props';
	import { tv } from '../tv';
	import type { Align, Position, RectCords } from '../types';

	export const popOutVariants = tv({ base: 'z-max inline-block max-h-[100vh] max-w-[100vw]' });

	export interface PopOutProps extends Omit<HTMLAttributes<HTMLElement>, 'class'> {
		align?: Align;
		alignOffset?: number;
		anchor?: RectCords;
		arrowNavigation?: boolean;
		children: Snippet;
		class?: ClassValue;
		closeOnEscape?: boolean;
		closeOnOutsideClick?: boolean;
		initialFocus?: boolean;
		offset?: number;
		onRequestClose?: () => void;
		position?: Position;
		ref?: HTMLElement | null;
		returnFocus?: boolean;
	}
</script>

<script lang="ts">
	let {
		align = 'Center',
		alignOffset = 0,
		anchor,
		arrowNavigation = true,
		children,
		class: className,
		closeOnEscape = true,
		closeOnOutsideClick = true,
		initialFocus = false,
		offset = POP_OUT_DEFAULT_OFFSET,
		onRequestClose,
		position = 'Bottom',
		ref = $bindable(null),
		returnFocus = true,
		...rest
	}: PopOutProps = $props();

	const customAnchor = $derived(
		anchor ? { getBoundingClientRect: () => DOMRect.fromRect(anchor) } : null
	);

	$effect(() => {
		if (!anchor) return;
		return overlayStore.acquire();
	});
</script>

{#if anchor}
	<Popover.Root
		bind:open={
			() => anchor !== undefined,
			(next) => {
				if (!next) onRequestClose?.();
			}
		}
	>
		<Popover.Portal to={PORTAL_CONTAINER_SELECTOR}>
			<!-- folds' PopOut root covered the viewport so the click that dismisses it never reaches the app. -->
			<div class="z-max inset-s0 fixed"></div>
			<Popover.Content
				align={POP_OUT_ALIGN[align]}
				{alignOffset}
				{customAnchor}
				side={POP_OUT_SIDE[position]}
				sideOffset={offset}
				{...layerProps({ closeOnEscape, closeOnOutsideClick, initialFocus, returnFocus })}
			>
				{#snippet child({ props, wrapperProps })}
					<div {...wrapperProps}>
						<div
							bind:this={ref}
							{...props}
							class={[popOutVariants(), className]}
							use:arrowKeyNavigation={arrowNavigation}
							{...rest}
						>
							{@render children()}
						</div>
					</div>
				{/snippet}
			</Popover.Content>
		</Popover.Portal>
	</Popover.Root>
{/if}
